import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { 
  UserPlus, 
  MoreVertical, 
  Mail, 
  RefreshCw, 
  X, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";
import { useProjectInvitations } from "@/hooks/useProjectInvitations";
import { InviteMemberDialog } from "./InviteMemberDialog";
import { ProjectInvitation } from "@/types/Invitation";
import { formatDistanceToNow } from "date-fns";

interface InvitationManagementProps {
  projectId: string;
  canManageInvitations: boolean;
}

export function InvitationManagement({ projectId, canManageInvitations }: InvitationManagementProps) {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const {
    invitations,
    isLoading,
    sendInvitation,
    revokeInvitation,
    resendInvitation,
    isSending,
    isRevoking,
    isResending,
  } = useProjectInvitations(projectId);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-warning" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
      case 'revoked':
        return <XCircle className="w-4 h-4 text-destructive" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'expired':
        return 'secondary';
      case 'revoked':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const handleInvite = async (email: string, role: 'admin' | 'member' | 'viewer') => {
    await sendInvitation({ email, role });
    setShowInviteDialog(false);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Invitations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            Loading invitations...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Invitations
            </CardTitle>
            <CardDescription>
              Manage project invitations and track their status.
            </CardDescription>
          </div>
          {canManageInvitations && (
            <Button onClick={() => setShowInviteDialog(true)} size="sm">
              <UserPlus className="w-4 h-4 mr-2" />
              Send Invitation
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {invitations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No invitations sent yet.</p>
            {canManageInvitations && (
              <p className="text-sm">Click "Send Invitation" to invite team members.</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {invitations.map((invitation) => (
              <InvitationCard
                key={invitation.id}
                invitation={invitation}
                canManage={canManageInvitations}
                onRevoke={revokeInvitation}
                onResend={resendInvitation}
                isRevoking={isRevoking}
                isResending={isResending}
                getStatusIcon={getStatusIcon}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        )}
      </CardContent>

      <InviteMemberDialog 
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        onInvite={handleInvite}
        isLoading={isSending}
      />
    </Card>
  );
}

interface InvitationCardProps {
  invitation: ProjectInvitation;
  canManage: boolean;
  onRevoke: (id: string) => Promise<void>;
  onResend: (invitation: ProjectInvitation) => Promise<void>;
  isRevoking: boolean;
  isResending: boolean;
  getStatusIcon: (status: string) => React.ReactNode;
  getStatusColor: (status: string) => string;
}

function InvitationCard({
  invitation,
  canManage,
  onRevoke,
  onResend,
  isRevoking,
  isResending,
  getStatusIcon,
  getStatusColor
}: InvitationCardProps) {
  const isExpired = invitation.expires_at < new Date();
  const actualStatus = isExpired && invitation.status === 'pending' ? 'expired' : invitation.status;

  return (
    <div className="flex items-center justify-between p-4 border rounded-lg">
      <div className="flex items-center gap-3">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="font-medium">{invitation.email}</span>
            <Badge variant={getStatusColor(actualStatus) as any} className="flex items-center gap-1">
              {getStatusIcon(actualStatus)}
              {actualStatus}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground">
            Role: <span className="font-medium">{invitation.role}</span>
            {invitation.inviter_name && (
              <> • Invited by {invitation.inviter_name}</>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {actualStatus === 'pending' ? (
              <>Expires {formatDistanceToNow(invitation.expires_at, { addSuffix: true })}</>
            ) : (
              <>Sent {formatDistanceToNow(invitation.created_at, { addSuffix: true })}</>
            )}
          </div>
        </div>
      </div>

      {canManage && actualStatus === 'pending' && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem 
              onClick={() => onResend(invitation)}
              disabled={isResending}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Resend Invitation
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => onRevoke(invitation.id)}
              disabled={isRevoking}
              className="text-destructive"
            >
              <X className="w-4 h-4 mr-2" />
              Revoke Invitation
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}